import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ref,
  set,
  push,
  query,
  orderByChild,
  equalTo,
  get,
} from "firebase/database";

import { auth, database } from "../firebase";
import { toast } from "react-toastify";
import { createUserWithEmailAndPassword } from "firebase/auth";
import AppLoader from "./AppLoader";

const Company = () => {
  const location = useLocation();
  const { email, password } = location.state || {};

  const [companyName, setCompanyName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [googleReviewLink, setGoogleReviewLink] = useState("");
  const [address, setAddress] = useState("");

  const [logoBase64, setLogoBase64] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Convert logo file to Base64 string
  const convertToBase64 = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoBase64(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      convertToBase64(file);
      setErrors((prev) => ({ ...prev, logo: "" })); // Clear logo error
    }
  };

  // Validate form inputs
  const validateInputs = () => {
    const newErrors = {};
    if (!companyName.trim())
      newErrors.companyName = "Company name is required.";
    if (!address.trim()) newErrors.address = "Company address is required.";
    if (!logoBase64) newErrors.logo = "Company logo is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle company creation
  const handleCompanyCreation = async () => {
    if (!validateInputs()) {
      toast.error("Please correct the highlighted errors.");
      return;
    }

    setLoading(true); // Show loader
    try {
      // Check if the company name already exists
      const companiesRef = query(
        ref(database, "companies"),
        orderByChild("companyName"),
        equalTo(companyName)
      );
      const snapshot = await get(companiesRef);

      if (snapshot.exists()) {
        setLoading(false);
        toast.error("A company with this name already exists.");
        return;
      }

      // Create a new user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      // Save company and user data to Firebase Database
      const companyRef = push(ref(database, "companies"));
      const companyId = companyRef.key;
      await set(companyRef, {
        Id: companyId,
        companyName,
        address,
        contactNumber,
        logo: logoBase64,
        googleReviewLink: googleReviewLink || "",
        createdAt: new Date().toISOString(),
        expiredAt: new Date(
          new Date().setMonth(new Date().getMonth() + 1)
        ).toISOString(),
      });

      const userRef = ref(database, "users/" + user.uid);
      await set(userRef, {
        email: user.email,
        uid: user.uid,
        companyId,
        role: "Admin",
        createdAt: new Date().toISOString(),
        isActive: true,
        isFirstLogin: true,
      });

      toast.success("Created successfully!");
      setLoading(false);
      navigate("/");
      auth.signOut();
    } catch (error) {
      setLoading(false);
      if (error.code === "auth/email-already-in-use") {
        toast.error(
          "This email is already in use. Please use a different email."
        );
      } else {
        toast.error("Error creating company. Please try again.");
      }
      console.error("Error creating company:", error);
    }
  };

  return (
    <div className="flex items-center justify-center h-screen">
      {loading && <AppLoader />} {/* Show loader while loading */}
      <div className="max-w-sm w-full p-6 bg-white rounded-lg">
        <h2 className="text-2xl font-bold mb-4">Create Company</h2>

        <div className="mb-4">
          <label
            htmlFor="companyName"
            className="block text-sm font-medium text-gray-700"
          >
            Company Name
          </label>
          <input
            id="companyName"
            type="text"
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              setErrors((prev) => ({ ...prev, companyName: "" }));
            }}
            className={`mt-1 block w-full p-2 border ${
              errors.companyName ? "border-red-500" : "border-gray-300"
            } rounded-md`}
            required
          />
          {errors.companyName && (
            <span className="text-red-500 text-sm">{errors.companyName}</span>
          )}
        </div>

        <div className="mb-4">
          <label
            htmlFor="address"
            className="block text-sm font-medium text-gray-700"
          >
            Company Address
          </label>
          <input
            id="address"
            type="text"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setErrors((prev) => ({ ...prev, address: "" }));
            }}
            className={`mt-1 block w-full p-2 border ${
              errors.address ? "border-red-500" : "border-gray-300"
            } rounded-md`}
            required
          />
          {errors.address && (
            <span className="text-red-500 text-sm">{errors.address}</span>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="contactNumber"
            className="block text-sm font-medium text-gray-700"
          >
            Contact Number
          </label>
          <input
            id="contactNumber"
            type="tel"
            value={contactNumber}
            onChange={(e) => {
              setContactNumber(e.target.value);
              setErrors((prev) => ({ ...prev, contactNumber: "" }));
            }}
            className={`mt-1 block w-full p-2 border ${
              errors.contactNumber ? "border-red-500" : "border-gray-300"
            } rounded-md`}
            required
          />
          {errors.contactNumber && (
            <span className="text-red-500 text-sm">{errors.contactNumber}</span>
          )}
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Google Review Link (Optional)
          </label>
          <input
            type="text"
            value={googleReviewLink}
            onChange={(e) => setGoogleReviewLink(e.target.value)}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="logo"
            className="block text-sm font-medium text-gray-700"
          >
            Company Logo
          </label>
          <input
            id="logo"
            type="file"
            onChange={handleLogoChange}
            className={`mt-1 block w-full p-2 border ${
              errors.logo ? "border-red-500" : "border-gray-300"
            } rounded-md`}
            accept="image/*"
          />
          {logoBase64 && (
            <div className="mt-2">
              <img
                src={logoBase64}
                alt="Logo Preview"
                className="w-20 h-20 object-cover"
              />
            </div>
          )}
          {errors.logo && (
            <span className="text-red-500 text-sm">{errors.logo}</span>
          )}
        </div>

        <button
          onClick={handleCompanyCreation}
          className="w-full py-2 mt-4 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600"
        >
          Create Company
        </button>
      </div>
    </div>
  );
};

export default Company;
